import { useState, useMemo } from "react";
import type { User, UserRole } from "@/mocks/users";
import { roleColors, mockDepartmentsList } from "@/mocks/users";

interface UsersTabProps {
  users: User[];
  onEdit: (user: User) => void;
  onAdd: () => void;
  onToggleStatus: (userId: string) => void;
  onDelete: (userId: string) => void;
}

function timeAgo(dateStr: string): string {
  const now = new Date();
  const then = new Date(dateStr);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return then.toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" });
}

export default function UsersTab({ users, onEdit, onAdd, onToggleStatus, onDelete }: UsersTabProps) {
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [sortField, setSortField] = useState<keyof User>("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(1);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const perPage = 8;

  const filtered = useMemo(() => {
    let result = [...users];
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(
        (u) =>
          u.name.toLowerCase().includes(s) ||
          u.email.toLowerCase().includes(s) ||
          u.department.toLowerCase().includes(s)
      );
    }
    if (deptFilter) result = result.filter((u) => u.department === deptFilter);
    if (roleFilter) result = result.filter((u) => u.role === roleFilter);

    result.sort((a, b) => {
      const va = a[sortField];
      const vb = b[sortField];
      if (sortField === "lastActive") {
        return sortDir === "asc"
          ? new Date(va as string).getTime() - new Date(vb as string).getTime()
          : new Date(vb as string).getTime() - new Date(va as string).getTime();
      }
      return sortDir === "asc"
        ? String(va).localeCompare(String(vb))
        : String(vb).localeCompare(String(va));
    });

    return result;
  }, [users, search, deptFilter, roleFilter, sortField, sortDir]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paged = filtered.slice((page - 1) * perPage, page * perPage);

  const handleSort = (field: keyof User) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const roleList: UserRole[] = ["Officer", "SCM Manager", "CFO/HOD", "System Admin"];

  return (
    <div>
      {/* Search & Filters */}
      <div className="bg-white rounded-lg border border-background-200 p-4 mb-4">
        <div className="flex flex-col sm:flex-row flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search by name, email, or department..."
              className="w-full h-9 pl-9 pr-3 border border-background-300 rounded-md text-sm text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            />
          </div>
          <select
            value={deptFilter}
            onChange={(e) => { setDeptFilter(e.target.value); setPage(1); }}
            className="h-9 px-3 border border-background-300 rounded-md text-sm text-foreground-700 bg-white focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 cursor-pointer"
          >
            <option value="">All Departments</option>
            {mockDepartmentsList.map((d) => (<option key={d} value={d}>{d}</option>))}
          </select>
          <select
            value={roleFilter}
            onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
            className="h-9 px-3 border border-background-300 rounded-md text-sm text-foreground-700 bg-white focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 cursor-pointer"
          >
            <option value="">All Roles</option>
            {roleList.map((r) => (<option key={r} value={r}>{r}</option>))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-background-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-background-200 bg-background-100">
                {[
                  { field: "name" as keyof User, label: "User" },
                  { field: "department" as keyof User, label: "Department" },
                  { field: "role" as keyof User, label: "Role", align: "center" as const },
                  { field: "status" as keyof User, label: "Status", align: "center" as const },
                  { field: "lastActive" as keyof User, label: "Last Active", align: "right" as const },
                  { field: "" as keyof User, label: "", align: "center" as const },
                ].map((col) => (
                  <th
                    key={col.field || "actions"}
                    onClick={() => col.field && handleSort(col.field)}
                    className={`text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap ${
                      col.field ? "cursor-pointer hover:text-foreground-700 select-none" : ""
                    } ${col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : ""}`}
                  >
                    {col.label && (
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        {sortField === col.field && (
                          <i className={`text-[10px] ${sortDir === "asc" ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}></i>
                        )}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paged.map((u) => {
                const rc = roleColors[u.role];
                return (
                  <tr
                    key={u.id}
                    className="border-b border-background-100 hover:bg-background-100 transition-colors cursor-pointer"
                    onClick={() => onEdit(u)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 text-[11px] font-semibold shrink-0">
                          {u.avatar}
                        </div>
                        <div className="min-w-0">
                          <p className="text-foreground-800 text-sm font-medium truncate">{u.name}</p>
                          <p className="text-foreground-400 text-xs truncate">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-foreground-600 text-sm">{u.department}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${rc.bg} ${rc.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${rc.dot}`}></span>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={(e) => { e.stopPropagation(); onToggleStatus(u.id); }}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${
                          u.status === "Active"
                            ? "bg-secondary-50 text-secondary-700"
                            : "bg-background-200 text-foreground-500"
                        }`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${u.status === "Active" ? "bg-secondary-500" : "bg-foreground-400"}`}></span>
                        {u.status}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right text-foreground-500 text-xs">{timeAgo(u.lastActive)}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); onEdit(u); }}
                          className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                          title="Edit user"
                        >
                          <i className="ri-pencil-line text-sm"></i>
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeleteConfirm(u.id); }}
                          className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-700 hover:bg-accent-50 transition-colors cursor-pointer"
                          title="Delete user"
                        >
                          <i className="ri-delete-bin-6-line text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {paged.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-foreground-400 text-sm">
                    No users match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-background-200">
            <p className="text-foreground-500 text-xs">
              Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, filtered.length)} of {filtered.length}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="w-8 h-8 rounded-md flex items-center justify-center border border-background-300 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-sm"></i>
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`w-8 h-8 rounded-md text-sm font-medium cursor-pointer ${
                    p === page
                      ? "bg-primary-600 text-white"
                      : "border border-background-300 text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="w-8 h-8 rounded-md flex items-center justify-center border border-background-300 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-sm"></i>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteConfirm(null)}></div>
          <div className="relative bg-white rounded-lg w-full max-w-[400px] mx-4 p-6">
            <div className="w-10 h-10 rounded-full bg-accent-50 flex items-center justify-center mx-auto mb-3">
              <i className="ri-error-warning-line text-accent-600 text-lg"></i>
            </div>
            <h3 className="text-foreground-800 text-base font-semibold text-center mb-1">Delete User</h3>
            <p className="text-foreground-500 text-sm text-center mb-5">
              This action cannot be undone. The user will lose all access.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="h-9 px-4 rounded-md text-sm font-medium text-foreground-600 border border-background-300 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={() => { onDelete(deleteConfirm); setDeleteConfirm(null); }}
                className="h-9 px-4 bg-accent-600 text-white text-sm font-semibold rounded-md hover:bg-accent-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}